// src/encounter/structured-note/note-phrases.ts
//
// PROD-2743 · frame v2 ONLY. Saved phrases (the provider's MACROS — `useNoteMacros`, GET
// /api/Doctor/GetMacros, own + shared + org-wide) inserted into a section's free-text narrative
// through the DS CommandMenu (`.`) and MacroMenu (contract §1b).
//
// The section narratives are PLAIN STRING answers in the QuestionnaireResponse (the `-narrative`
// item, PROD-1368), not Quill documents. So:
//   · a phrase inserts its TEXT only; phrase metadata (`cps-phrase`) is never inserted;
//   · a phrase that carries PIECES (pick-lists, blanks, chart links — PR #553) is NOT offered here:
//     a plain string cannot hold a piece, and inserting its open display would leave
//     "[not completed: …]" text that no F2 can find. Pieces stay in the A&P narrative, where the
//     codec lives (`?ef=phrasePieces`);
//   · a phrase that names its editors (`cps-phrase.editors`) is offered only in those editors; one
//     that names none (every legacy macro) is offered everywhere — the same rule as the A&P note.
// A phrase writes narrative only, never a diagnosis, order, charge or structured answer (rule 3).
import { AUTHOR_ROLE_LABEL, type AuthorRole, type CommandKind, type CommandMenuItem, type NoteMacro as DsNoteMacro } from '@scripsteam/scrips-signal-ds';
import { deltaPlainText, parseNoteOps } from '../lib/note-delta';
import { PIECE_ATTR, parsePiece, pieceDisplay, toDefinition } from '../lib/note-pieces';
import { definitionPieceCount, isOfferedIn, matchMacros, phraseLabel, typeableTrigger, type NoteMacro } from '../lib/smart-phrase';
import { suggestedFirst } from '../complaint-map/phrases-first';
import { ALLIED_EDITORS, DISCIPLINE_EDITOR, type AlliedDiscipline } from '../lib/allied-discipline';

/** The editor id a section narrative answers to in `cps-phrase.editors`. */
export const narrativeEditor = (step: string): string => `${step}-narrative`;

// ── PROD-2824 · TS-5: phrases in every note line, each offered where it is targeted ─────────────
// The validated phrase library (Templates lane, `phrase-library-v1.json`) names the note EDITORS it is
// written for. Two of them are lines of this note:
//   · `chief-complaint-note` — the HPI step IS the encounter's "Chief Complaint" screen (the step title,
//     encounter-shell-view.tsx `DESIGN_SECTION_TITLE`), so every HPI line answers to it;
//   · `nursing-note` — a nurse's lines in S and O are her nursing note.
// A line still answers to its own `<step>-narrative`. A phrase that names other editors only is not
// offered (PROD-2824 acceptance); one that names none (every legacy macro) is offered everywhere.
// Allied-health disciplines' editors (therapy, dietitian, exercise physiology, coaching, physio) follow
// the clinician's specialty, which this view does not know: unchanged, routed to the encounter owner.

/** The library's chief-complaint note editor (answered by every HPI line). */
export const CHIEF_COMPLAINT_EDITOR = 'chief-complaint-note';
/** The library's nursing note editor (answered by every line a nurse writes). */
export const NURSING_NOTE_EDITOR = 'nursing-note';
/** PROD-3148: Wrap-up › Patient instructions answers to the wrap-up step's lines (`lineEditors('wrap-up', …)`) and to
 *  this editor, so a library can hold phrases written for patients. */
export const PATIENT_INSTRUCTIONS_EDITOR = 'patient-instructions';
/** The editors the A&P "Added notes" line answers to (the narrative the pop-up wrote). */
export const AP_NARRATIVE_EDITORS: readonly string[] = ['ap-narrative'];

/** The editors a line of `step` answers to, for this author. PROD-2947 (Andrew's walk, 7 Oct, PROD-3055 b): an
 *  allied-health author's lines also answer to her discipline's note editor (physio-note, ep-note, dietitian-note,
 *  coaching-note, therapy-note — the allied libraries' targets); with no known discipline, to all of them. */
export function lineEditors(step: string, author: AuthorRole, discipline?: AlliedDiscipline): string[] {
  const editors = [narrativeEditor(step)];
  if (step === 'hpi') editors.push(CHIEF_COMPLAINT_EDITOR);
  if (author === 'nurse') editors.push(NURSING_NOTE_EDITOR);
  if (author === 'allied-health') editors.push(...(discipline ? [DISCIPLINE_EDITOR[discipline]] : ALLIED_EDITORS));
  return editors;
}

/**
 * Can a line RENDER this phrase's pieces — every `cps-piece` run a v:1 pick-list, blank or chart link
 * this engine reads, and its text still the piece's open display (the same checksum rule as
 * `phraseInsertOps`)? A run that fails either would land as its literal text — "[not completed: …]",
 * a plain-text copy that can bring back pre-filled sample findings — so such a phrase is never offered
 * in a line (TS-5, Omics path: "a phrase that a section can't render is not offered there").
 */
export function phraseRenderable(storedValue: string): boolean {
  const ops = parseNoteOps(storedValue);
  for (let i = 0; i < ops.length; i++) {
    const raw = ops[i].attributes?.[PIECE_ATTR];
    if (raw === undefined) continue;
    let text = typeof ops[i].insert === 'string' ? (ops[i].insert as string) : undefined;
    while (text !== undefined && i + 1 < ops.length && ops[i + 1].attributes?.[PIECE_ATTR] === raw) {
      const next = ops[++i].insert;
      text = typeof next === 'string' ? text + next : undefined;
    }
    const def = parsePiece(raw);
    if (!def || text === undefined || text !== pieceDisplay(toDefinition(def))) return false;
  }
  return true;
}

/**
 * The phrases a line may take: offered in one of its editors, and — unless pieces are switched on
 * (`?ef=phrasePieces`, the same switch the A&P note reads) — carrying no piece. With pieces on, a piece
 * phrase opens as an occurrence and lands as prose (phrase-occurrence.tsx) — but only if the line can
 * render every one of its pieces (`phraseRenderable`). Never a plain-text copy of a piece phrase.
 */
export function offeredPhrasesIn(macros: readonly NoteMacro[], editors: readonly string[], opts: { pieces?: boolean } = {}): NoteMacro[] {
  return macros.filter((m) => {
    if (!editors.some((e) => isOfferedIn(m.value, e))) return false;
    if (definitionPieceCount(m.value) === 0) return true;
    return !!opts.pieces && phraseRenderable(m.value);
  });
}

/** The phrases a plain narrative may take: offered in this editor, and carrying no piece. */
export function offeredPhrases(macros: readonly NoteMacro[], editor: string): NoteMacro[] {
  return offeredPhrasesIn(macros, [editor]);
}

/** The only menu rows a note line ever offers: narrative (a phrase, or a structured template when one exists). A diagnosis, a lab
 *  order or a referral is an A&P activity (C3), never narrative — and never offered to a nurse. */
const NARRATIVE_KINDS: ReadonlySet<CommandKind> = new Set<CommandKind>(['Phrase', 'Template']);

/** `items` as a note line may offer them to `author`: narrative rows only, and only rows the author may
 *  issue (TS-5: "/" never offers an order row to a nurse, whatever the library holds). */
export function lineCommandItems(items: readonly CommandMenuItem[], author: AuthorRole): CommandMenuItem[] {
  return items.filter((it) => NARRATIVE_KINDS.has(it.kind) && it.issuableBy.includes(author));
}

/** Does this phrase carry pieces (a pick-list, a blank, a chart link) that must be filled first? */
export const carriesPieces = (macro: NoteMacro): boolean => definitionPieceCount(macro.value) > 0;

/** A stored phrase body (Delta ops) as plain text: its text runs, embeds dropped, the terminal
 *  newline trimmed. A body that does not parse falls back to its stored plain preview. */
export function phrasePlainText(macro: NoteMacro): string {
  const text = deltaPlainText(parseNoteOps(macro.value)).replace(/\n+$/, '');
  return text || macro.preview;
}

/**
 * CommandMenu rows. `.` lists the library as Phrases (found by trigger). A phrase is NEVER listed under `/`:
 * `/` is for structured templates (their own 'Template' rows, `toTemplateCommandItems`) and, in the A&P,
 * the Diagnosis / Lab order / Referral rows (ap-add-line.tsx). So a narrative line passes Phrase rows only.
 * A phrase inserts text only, so every role may use it.
 */
export function toCommandItems(macros: readonly NoteMacro[]): CommandMenuItem[] {
  // Every author role (the DS's own role list): a phrase is narrative, which any clinician may write.
  const issuableBy = Object.keys(AUTHOR_ROLE_LABEL) as AuthorRole[];
  // L8 / F5: human names ("Copy goal", never ".copy:-goal") and triggers a clinician can actually type.
  return macros.map((m) => ({ id: m.id, kind: 'Phrase' as const, trigger: `.${typeableTrigger(m.trigger)}`, label: phraseLabel(m), hint: m.preview || undefined, issuableBy }));
}

/** A template a note line may add as its own answer set: the title the menu shows and the id the host inserts. */
export interface InsertableTemplate {
  id: string;
  title: string;
}

/** Id prefix of a Template row, so a pick is told from a phrase and the questionnaire id read back. */
const TEMPLATE_ROW_PREFIX = 'tpl:';

/** The questionnaire id of a Template row from {@link toTemplateCommandItems}; undefined for any other row. */
export function templateRowQuestionnaireId(item: CommandMenuItem): string | undefined {
  return item.kind === 'Template' && item.id.startsWith(TEMPLATE_ROW_PREFIX) ? item.id.slice(TEMPLATE_ROW_PREFIX.length) : undefined;
}

/**
 * CommandMenu rows for the templates a clinician may add: `/` lists them (a phrase never goes under `/`, a template
 * never under `.`; the menu keys on the row's kind). Adding a template writes nothing into the line: it starts an
 * answer set of its own. Any author role may add one, like a phrase.
 */
export function toTemplateCommandItems(templates: readonly InsertableTemplate[]): CommandMenuItem[] {
  const issuableBy = Object.keys(AUTHOR_ROLE_LABEL) as AuthorRole[];
  return templates.map((t) => ({ id: `${TEMPLATE_ROW_PREFIX}${t.id}`, kind: 'Template' as const, label: t.title, issuableBy }));
}

/** MacroMenu rows: the same phrases, own first (MacroMenu's "Primary" tag is not used: the library
 *  has no primary flag, and none is invented). PROD-2947 Part 2 (G8): `suggested` triggers (the visit's chief
 *  complaint, starter-map order) come before every other row, in their order; nothing is inserted. */
export function toMenuMacros(macros: readonly NoteMacro[], suggested: readonly string[] = []): DsNoteMacro[] {
  return suggestedFirst(matchMacros([...macros], '', Number.MAX_SAFE_INTEGER), suggested).map((m) => ({
    id: m.id,
    name: phraseLabel(m),
    hint: m.preview || (m.scope === 'own' ? 'Your phrase' : m.scope === 'shared' ? 'Shared with you' : 'Clinic phrase'),
    phrase: `.${typeableTrigger(m.trigger)}`,
    personal: m.scope === 'own' || undefined,
  }));
}

/** NoteFieldLine's `.` / `/` token (signal-ds NoteFieldLine `COMMAND_TOKEN`): it starts the text or follows whitespace. */
const COMMAND_TOKEN = /(^|\s)([./]\S*)$/;

/** PROD-3148: where the token that opened the phrase menu starts, as an offset in the line's text, read from the text
 *  up to the caret at the moment of the pick (NoteFieldLine asks `onInsert` BEFORE it removes the token, so the text
 *  before this offset is the same after the removal). Undefined when no token ends at the caret. */
export function triggerStart(text: string, caret: number): number | undefined {
  const m = COMMAND_TOKEN.exec(text.slice(0, Math.max(0, Math.min(caret, text.length))));
  return m ? Math.min(caret, text.length) - m[2].length : undefined;
}

/** Where a phrase called with `.` goes: the token's start (`at`), and the line's text on each side of the token, so
 *  the place is found again if the line is edited while the phrase is being filled (Codex P2 on PROD-3148). */
export interface TriggerAnchor {
  at: number;
  before: string;
  after: string;
  /** PROD-3149: placed at a CURSOR (the block's Macros), not at a typed `.`: a space is added on a side where the phrase
   *  would otherwise touch a word. A typed `.` sits where the clinician typed it, so it is never padded. */
  pad?: boolean;
}

/** The anchor of the token ending at the caret, from the line's text at the moment of the pick (token still there). */
export function triggerAnchor(text: string, caret: number): TriggerAnchor | undefined {
  const at = triggerStart(text, caret);
  if (at === undefined) return undefined;
  return { at, before: text.slice(0, at), after: text.slice(Math.min(caret, text.length)) };
}

/** The same, read from the line's field by its id (NoteFieldLine puts `id` on its textarea). */
export function triggerAnchorIn(fieldId: string): TriggerAnchor | undefined {
  const el = typeof document === 'undefined' ? null : document.getElementById(fieldId);
  if (!(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)) return undefined;
  return triggerAnchor(el.value, el.selectionStart ?? el.value.length);
}

/** The cursor each text field had when it last lost focus. A field the clinician never focused has none: its
 *  `selectionStart` is a browser default (often 0), not a place anyone chose. */
const lastCaret = new WeakMap<Element, { caret: number; value: string }>();
let tracking = false;

/** Start recording field cursors (idempotent). The block's Macros calls it on mount, before any field is touched. */
export function trackFieldCarets(): void {
  if (tracking || typeof document === 'undefined') return;
  tracking = true;
  document.addEventListener(
    'focusout',
    (e) => {
      const t = e.target;
      if (t instanceof HTMLTextAreaElement || t instanceof HTMLInputElement) lastCaret.set(t, { caret: t.selectionStart ?? t.value.length, value: t.value });
    },
    true,
  );
}

/** PROD-3149: the cursor the clinician left in the line's field, for the block's Macros (Apply inserts there): the live
 *  cursor while the field has focus, else the one it had when it lost focus. A field never focused, or no field on
 *  screen, has no anchor (the phrase is appended on its own line). A cursor at the end puts the phrase there, on the
 *  same line (insertPhrase / spliceAt), as a typed `.` at the end does (PROD-3166). */
export function caretAnchorIn(fieldId: string): TriggerAnchor | undefined {
  const el = typeof document === 'undefined' ? null : document.getElementById(fieldId);
  if (!(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)) return undefined;
  if (document.activeElement === el) {
    const at = Math.min(el.selectionStart ?? el.value.length, el.value.length);
    return { at, before: el.value.slice(0, at), after: el.value.slice(at), pad: true };
  }
  const left = lastCaret.get(el);
  if (!left) return undefined;
  // Codex P2: the text may have changed since the field was left (another writer, a phrase). The cursor is kept as the
  // text around it, and found again in the text as it is now (placeAt); where it cannot be found, there is no cursor.
  const at0 = Math.min(left.caret, left.value.length);
  const kept: TriggerAnchor = { at: at0, before: left.value.slice(0, at0), after: left.value.slice(at0), pad: true };
  const at = left.value === el.value ? at0 : placeAt(kept, el.value);
  return at === undefined ? undefined : { at, before: el.value.slice(0, at), after: el.value.slice(at), pad: true };
}

/** `body` put into `text` at `at`, on the same line. A space where it would touch a word: on both sides at a cursor
 *  (`pad`, the block's Macros, PROD-3149); for a typed `.` only at the END, after a word whose space was lost (PROD-3166,
 *  see `placeAt`). Mid-line a typed `.` goes exactly at its place. The same rule as `insertPhrase` (note-line-delta.ts). */
export function spliceAt(text: string, at0: number, body: string, pad = false): string {
  // A position past the end is the end, as in insertPhrase (Codex P2: the two must agree for any position).
  const at = Math.max(0, Math.min(at0, text.length));
  const before = text.slice(0, at);
  const after = text.slice(at);
  const words = body.replace(/\n+$/, '');
  // A space only where a word would touch a word: neither side, nor the phrase's own edge, is already a space (Codex P3).
  const left = (pad || at === text.length) && before && !/\s$/.test(before) && !/^\s/.test(words) ? ' ' : '';
  const right = pad && after && !/^\s/.test(after) && !/\s$/.test(words) ? ' ' : '';
  return before + left + words + right + after;
}

/** The anchor's offset in the line's text NOW: unchanged around it → its place; text added or removed only after
 *  it → the end of the text before it; only before it → the start of the text after it. When both sides changed, or
 *  the line was empty, there is no safe place: undefined, and the phrase is appended (the Macros behaviour). */
export function placeAt(anchor: TriggerAnchor, text: string): number | undefined {
  const { before, after } = anchor;
  // Both sides kept (anything new sits between them): after `before`. An empty side is no evidence (Codex P2: with
  // nothing before the trigger, `startsWith('')` would pin the phrase to 0 ahead of text added there).
  if (before && after && text.length >= before.length + after.length && text.startsWith(before) && text.endsWith(after)) return before.length;
  if (before && text.startsWith(before)) return before.length;
  if (after && text.endsWith(after)) return text.length - after.length;
  // `.` typed at the end, and the line as read now has lost the space typed before it (a copy that lags the field, or
  // a save that trims): the place is still the end, on the same line (PROD-3166). The insert joins with one space.
  const kept = before.trimEnd();
  if (kept && !after && kept !== before && text.trimEnd() === kept) return text.length;
  return undefined;
}

/** Insert `body` at `cursor` (the block's Macros appends: cursor = the text's end), on its own line when
 *  the narrative already has text. The `.` / `/` token inside a field is NoteFieldLine's own (`commands`). */
export function insertAt(text: string, cursor: number, body: string): { text: string; cursor: number } {
  const at = Math.max(0, Math.min(cursor, text.length));
  const before = text.slice(0, at);
  const sep = before && !/\s$/.test(before) ? '\n' : '';
  const next = before + sep + body + text.slice(at);
  return { text: next, cursor: at + sep.length + body.length };
}
